import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUsersStore } from '@/lib/stores/users';
import { useToast } from '@/hooks/use-toast';

export default function UserManagement() {
  const { users, isLoading, error, fetchUsers, updateUser } = useUsersStore();
  const { toast } = useToast();
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Set up real-time subscription for profile updates
  useEffect(() => {
    const channel = supabase
      .channel('profiles-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          console.log('Profile change detected:', payload);
          // Refresh users when any profile changes
          fetchUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchUsers]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      setUpdatingUserId(userId);
      
      const userToUpdate = users.find(user => user.id === userId);
      if (!userToUpdate) {
        throw new Error('User not found');
      }

      const updatedUser = {
        ...userToUpdate,
        role: newRole as "admin" | "doctor" | "staff"
      };

      await updateUser(updatedUser);
      
      toast({
        title: "Role Updated",
        description: `User role has been updated to ${newRole}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (isLoading) return <div className="flex items-center justify-center p-8">Loading users...</div>;
  if (error) return <div className="text-red-500 p-8">Error: {error}</div>;

  return (
    <div className="container mx-auto py-10">
      <h1 className="text-2xl font-bold mb-6">User Management</h1>
      
      <Table>
        <TableHeader>
          <TableRow>
                          <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
                              <TableCell className="font-medium">{user.name || 'N/A'}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell className="capitalize">{user.role}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    user.status === "active"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}>
                    {user.status}
                  </span>
                </TableCell>
                <TableCell>
                <div className="space-x-2">
                  {user.role !== 'admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRoleChange(user.id, 'admin')}
                      disabled={updatingUserId === user.id}
                    >
                      {updatingUserId === user.id ? 'Updating...' : 'Make Admin'}
                    </Button>
                  )}
                  {user.role !== 'staff' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRoleChange(user.id, 'staff')}
                      disabled={updatingUserId === user.id}
                    >
                      {updatingUserId === user.id ? 'Updating...' : 'Make Staff'}
                    </Button>
                  )}
                  {user.role !== 'doctor' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRoleChange(user.id, 'doctor')}
                      disabled={updatingUserId === user.id}
                    >
                      {updatingUserId === user.id ? 'Updating...' : 'Make Doctor'}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
} 